// ─────────────────────────────────────────────────────
// Style Page — Color analysis, style profile, recommendations
// ─────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { api } from "../services/api";

type ColorInfo = { name: string; hex: string };
type Recommendation = { name: string; brand: string; match: number; emoji: string; reason: string };
type ArchetypeBar = { label: string; score: number };
type MeasurementRow = [string, string];

// Fallback data when API hasn't populated profile yet
const FALLBACK_COLORS: ColorInfo[] = [
  { name: "Burgundy", hex: "#800020" }, { name: "Navy", hex: "#1B2838" },
  { name: "Forest", hex: "#2E4B3F" }, { name: "Camel", hex: "#C19A6B" },
  { name: "Ivory", hex: "#FFFFF0" }, { name: "Terracotta", hex: "#CC6644" },
];

export function StylePage() {
  const [loading, setLoading] = useState(true);
  const [colorSeason, setColorSeason] = useState("—");
  const [bestColors, setBestColors] = useState<ColorInfo[]>(FALLBACK_COLORS);
  const [avoidColors, setAvoidColors] = useState<ColorInfo[]>([]);
  const [archetype, setArchetype] = useState("—");
  const [archetypeBars, setArchetypeBars] = useState<ArchetypeBar[]>([]);
  const [bodyType, setBodyType] = useState("—");
  const [measurements, setMeasurements] = useState<MeasurementRow[]>([]);
  const [lastScan, setLastScan] = useState("");
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [styleProfile, recs, meas] = await Promise.allSettled([
          api.getStyleProfile(),
          api.getRecommendations(),
          api.getMeasurements(),
        ]);

        if (styleProfile.status === "fulfilled" && styleProfile.value) {
          const sp = styleProfile.value;
          setColorSeason(sp.color_season || sp.colorSeason || "—");
          setArchetype(sp.style_archetype || sp.styleArchetype || "—");
          setBodyType(sp.body_type || sp.bodyType || "—");

          if (sp.complementary_colors?.length || sp.bestColors?.length) {
            const colors = sp.complementary_colors || sp.bestColors || [];
            setBestColors(colors.map((c: string | ColorInfo) =>
              typeof c === "string" ? { name: c, hex: c.startsWith("#") ? c : "#888" } : c
            ));
          }
          if (sp.avoid_colors?.length || sp.avoidColors?.length) {
            const avoid = sp.avoid_colors || sp.avoidColors || [];
            setAvoidColors(avoid.map((c: string | ColorInfo) =>
              typeof c === "string" ? { name: c, hex: c.startsWith("#") ? c : "#888" } : c
            ));
          }
          if (sp.archetype_scores || sp.archetypeBars) {
            const scores = sp.archetype_scores || sp.archetypeBars || {};
            setArchetypeBars(
              Object.entries(scores).map(([label, score]) => ({ label, score: score as number }))
            );
          }
        }

        if (recs.status === "fulfilled" && Array.isArray(recs.value)) {
          setRecommendations(recs.value.map((r: Record<string, unknown>) => ({
            name: r.name as string || (r.garments as { name: string }[])?.[0]?.name || "Outfit",
            brand: r.brand as string || r.source as string || "SXT",
            match: Math.round(((r.score ?? r.match ?? 0.9) as number) * 100),
            emoji: r.emoji as string || "👔",
            reason: r.rationale as string || r.reason as string || "",
          })));
        }

        if (meas.status === "fulfilled" && meas.value) {
          const m = meas.value;
          const rows: MeasurementRow[] = [];
          if (m.height) rows.push(["Height", `${m.height} cm`]);
          if (m.chest) rows.push(["Chest", `${m.chest} cm`]);
          if (m.waist) rows.push(["Waist", `${m.waist} cm`]);
          if (m.hips) rows.push(["Hips", `${m.hips} cm`]);
          if (m.shoulders) rows.push(["Shoulders", `${m.shoulders} cm`]);
          if (m.inseam) rows.push(["Inseam", `${m.inseam} cm`]);
          if (m.neck) rows.push(["Neck", `${m.neck} cm`]);
          setMeasurements(rows);
          if (m.created_at || m.scan_date) setLastScan(m.created_at || m.scan_date);
        }
      } catch {
        // Keep defaults on failure
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-sxt-primary animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-3xl font-bold mb-8">Style Profile</h2>

      <div className="grid grid-cols-3 gap-6 mb-8">
        {/* Color Analysis */}
        <div className="bg-sxt-surface rounded-2xl p-6 border animate-fade-in">
          <h3 className="text-lg font-bold mb-1">Color Season</h3>
          <p className="text-sxt-primary text-2xl font-bold mb-4">{colorSeason}</p>

          <p className="text-sm text-sxt-muted mb-3">Your Best Colors</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {bestColors.map((c) => (
              <div key={c.name} className="group relative">
                <div
                  className="w-10 h-10 rounded-xl border-2 border-transparent hover:border-sxt-primary/50 transition-colors cursor-pointer"
                  style={{ backgroundColor: c.hex }}
                />
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-sxt-muted opacity-0 group-hover:opacity-100 whitespace-nowrap transition-opacity">
                  {c.name}
                </span>
              </div>
            ))}
          </div>

          {avoidColors.length > 0 && (
            <>
              <p className="text-sm text-sxt-muted mb-3 mt-4">Colors to Avoid</p>
              <div className="flex gap-2">
                {avoidColors.map((c) => (
                  <div key={c.name} className="relative">
                    <div className="w-10 h-10 rounded-xl opacity-50" style={{ backgroundColor: c.hex }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white text-lg font-bold drop-shadow">&#x2715;</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Style Archetype */}
        <div className="bg-sxt-surface rounded-2xl p-6 border animate-fade-in">
          <h3 className="text-lg font-bold mb-1">Style Archetype</h3>
          <p className="text-sxt-primary text-2xl font-bold mb-4">{archetype}</p>

          <div className="space-y-4">
            {archetypeBars.map((bar) => (
              <div key={bar.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-sxt-muted">{bar.label}</span>
                  <span className="text-sxt-primary font-semibold">{bar.score}%</span>
                </div>
                <div className="h-2 bg-sxt-border rounded-full overflow-hidden">
                  <div className="h-full bg-sxt-primary rounded-full" style={{ width: `${bar.score}%` }} />
                </div>
              </div>
            ))}
            {archetypeBars.length === 0 && (
              <p className="text-sm text-sxt-dim">Complete a style analysis to see your archetype breakdown</p>
            )}
          </div>
        </div>

        {/* Body Profile */}
        <div className="bg-sxt-surface rounded-2xl p-6 border animate-fade-in">
          <h3 className="text-lg font-bold mb-1">Body Profile</h3>
          <p className="text-sxt-primary text-2xl font-bold mb-4">{bodyType}</p>

          <div className="space-y-3">
            {measurements.map(([label, value]) => (
              <div key={label} className="flex justify-between py-1 border-b border-sxt-border/50">
                <span className="text-sm text-sxt-muted">{label}</span>
                <span className="text-sm font-semibold">{value}</span>
              </div>
            ))}
            {measurements.length === 0 && (
              <p className="text-sm text-sxt-dim">Complete a body scan to see your measurements</p>
            )}
          </div>
          {lastScan && <p className="text-xs text-sxt-dim mt-4">Last scan: {lastScan}</p>}
        </div>
      </div>

      {/* Recommendations */}
      <div className="flex items-center gap-3 mb-6">
        <Sparkles className="w-6 h-6 text-sxt-primary" />
        <h3 className="text-xl font-bold">AI Recommendations</h3>
      </div>
      {recommendations.length > 0 ? (
        <div className="grid grid-cols-3 gap-4">
          {recommendations.map((r) => (
            <div key={r.name} className="bg-sxt-surface rounded-2xl p-5 border hover:border-sxt-primary/50 transition-colors animate-fade-in">
              <div className="flex items-start justify-between mb-3">
                <span className="text-3xl">{r.emoji}</span>
                <span className="text-xs font-bold bg-sxt-primary/15 text-sxt-primary px-2 py-1 rounded-lg">
                  {r.match}% match
                </span>
              </div>
              <p className="text-sm font-semibold">{r.name}</p>
              <p className="text-xs text-sxt-muted mt-1">{r.brand}</p>
              <p className="text-xs text-sxt-dim mt-2">{r.reason}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-sxt-surface rounded-2xl border">
          <Sparkles className="w-10 h-10 text-sxt-dim mx-auto mb-3" />
          <p className="text-sxt-muted">No recommendations yet</p>
          <p className="text-sm text-sxt-dim mt-1">Complete your style profile and body scan to get AI-powered picks</p>
        </div>
      )}
    </div>
  );
}
